// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## scalePlay.js
//
// Play scales on the piano
import InteractivePrototype from './interactivePrototype.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import config from '../config.ts';

class ScalePlay extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeed =
      this.setTargetSpeed(ScalePlay.registry.map((r) => r.skills).reduce((a, b) => a.concat(b))) /
      8;
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegions: 'all',
      },
      piano: {
        ariaLive: options.testMode ? 'assertive' : 'off',
      },
      checkMark: { rightAnswerLength: 1.2 },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    options.maxQuestions = options.maxQuestions || 5;

    // If we're in testMode, we need to give them the option to
    // undo a click & submit when ready.
    if (options.testMode) {
      this.settings.letters = {
        letterButtons: false,
        customButtons: [
          { text: 'undo', hotKey: 'delete', value: 'undo' },
          { text: 'submit', hotKey: 'enter', value: 'submit' },
        ],
      };

      this.checkpointHighlightedKeys = [];
    }
    this.showNumbers = !!options.showNumbers;
    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    // In non drill mode, specify questions as:
    //  keynote/mode/asc or dsc
    //  modes: Major, minor, harmonicMinor, melodicMinor
    if (this.options.testMode) return;

    if (this.optionsTrue('drill')) {
      this.randomQuestionGenerator();
    } else {
      this.processManualQuestions();
    }

    this.prepareQuestions();
  }

  // read in this.options.questions:
  processManualQuestions(questions) {
    questions = questions || this.options.questions;
    this.scales = questions.map((question) => {
      const parts = question.split('/');
      const mode = parts[1];
      const note = utils.isNumeric(parts[0])
        ? new Notation.KeySignature(parts[0], mode.includes('m') ? 'minor' : 'Major').keyNote
        : new Notation.Note(parts[0]);
      const direction = typeof parts[2] !== 'undefined' && parts[2] === 'dsc' ? -1 : 1;
      return new Notation.Scale(note, mode, direction);
    }, this);
  }

  prepareQuestions() {
    this.questions = [];
    this.scales.forEach((scale) => {
      const scaleText = scale.toAriaString({}, true);
      const text = `${utils.aOrAn(scaleText)} ${scaleText}`;
      const skill = this.calculateSkill(scale.mode);
      const questionObject = {
        scale,
        text: [text, this.questions.length + 1],
        skills: [skill],
        direction: scale.direction,
      };
      this.questions.push(questionObject);
    }, this);
    // set up global variables:
    if (this.optionsTrue('questionPrompt')) {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt = 'Play {question} scale.';
    }
    this.maxQuestions = this.questions.length;
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>#{question} of ${this.maxQuestions}</small>`;
    }
    this.questionNumber = 0;
    this.scaleDegree = 0;
  }

  calculateSkill(mode) {
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    return this.skillCalculator.getPianoScaleSkill(mode);
  }

  // or generate random questions:
  randomQuestionGenerator() {
    const generator = new QuestionGenerator();
    this.scales = generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions,
      this.options
    );
    // randomize direction:
    this.scales.forEach((scale) => {
      if (Math.random() > 0.5) {
        scale.setDirection(-1);
      }
    });
  }

  addListeners() {
    const pianoListener = this.options.testMode ? this.checkPointNotePress : this.checkAnswer;
    this.piano.addNoteOnListener(pianoListener, this);

    if (this.options.testMode) {
      this.letterPanel.addCustomButtonCallback((value) => {
        if (value === 'undo') this.undo();
        if (value === 'submit') this.checkpointAnswer();
      }, this);
    }
  }

  // ## Display new question / increment:

  newQuestion() {
    this.question = this.questions[this.questionNumber];
    const tonic = this.question.scale.notes[0];
    const lowest = Notation.Interval.getNoteAtGenericInterval(tonic, -3);
    const highest = Notation.Interval.getNoteAtGenericInterval(tonic, 10);
    this.piano.setBoundaries(lowest, highest);

    this.scaleDegree = this.question.direction === 1 ? 0 : 7;
    super.newQuestion();
  }

  // ## Setup new checkpoint question:
  newCheckpointQuestion(question) {
    return this.loadingPromise.then(() => {
      this.checkpointHighlightedKeys.length = 0;
      this.piano.clearColors();
      this.piano.clearNoteLabels();
      this.questionNumber = 0;
      this.processManualQuestions([question]);
      this.prepareQuestions();
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  // ## Checking & Processing Routines

  checkAnswer(midiNumber) {
    if (this.rightAnswerDelay) return;
    const goalNote = this.question.scale.notes[this.scaleDegree];
    if (!goalNote) return;
    this.checkMark.off();
    const goalMidi = goalNote.getMidiNumber();
    // Right answer:
    if (goalMidi === midiNumber) this.rightScaleDegree(midiNumber);
    // Right answer, wrong octave:
    else if (!this.isWrongOctave(midiNumber)) {
      this.piano.colorNote(midiNumber, 'red');
      window.setTimeout(() => {
        this.piano.clearNoteColoring(midiNumber);
      }, 500);
      this.wrongAnswer();
    }
  }

  isWrongOctave(midiNumber) {
    if (this.scaleDegree % 7 !== 0) return false;
    const goalNote = this.question.scale.notes[this.scaleDegree];
    const goalMidi = goalNote.getMidiNumber();

    if (goalMidi !== midiNumber && goalMidi % 12 === midiNumber % 12) {
      const direction = goalMidi < midiNumber ? 'lower' : 'higher';
      this.checkMark.warn(`Start an octave ${direction}.`);
      return true;
    }
    return false;
  }

  checkPointNotePress(midiNumber) {
    if (this.checkpointHighlightedKeys.length > 7) return;
    if (this.isWrongOctave(midiNumber)) return;
    // If it's the same note as the last note, might be an accidental
    // double click
    if (
      this.checkpointHighlightedKeys.length &&
      this.checkpointHighlightedKeys[this.checkpointHighlightedKeys.length - 1] === midiNumber
    )
      return;

    const stack = this.checkpointHighlightedKeys;

    // Unhighlight previous note, highlight & label new one:
    if (stack.length) this.piano.clearNoteColoring(stack[stack.length - 1]);
    this.checkpointHighlightedKeys.push(midiNumber);
    this.piano.labelNote(midiNumber, this.scaleDegree + 1);
    this.piano.colorNote(midiNumber);

    // Update scaleDegree
    this.scaleDegree += this.question.direction;
  }

  /* checkpoints only: removes last highlighted note:
   * @param none
   */
  undo() {
    if (!this.checkpointHighlightedKeys.length) return;

    // Update scale degree:
    this.scaleDegree -= this.question.direction;

    // Correct highlighting & labels, and remove last answer from stack:
    const stack = this.checkpointHighlightedKeys;
    const lastNote = stack.pop();
    this.piano.clearNoteColoring(lastNote);
    this.piano.clearNoteLabel(lastNote);
    if (stack.length) this.piano.colorNote(stack[stack.length - 1]);
  }

  getAnswerAndData() {
    let weightedRightAnswers = 0;
    let weightedWrongAnswers = 0;
    const stack = this.checkpointHighlightedKeys;
    const red = config.Colors.lightRed;
    // Check scale & mark wrong answers:
    this.piano.clearColors();
    const midis = this.question.scale.notes.map((note) => note.getMidiNumber());
    if (this.question.direction === -1) midis.reverse();
    midis.forEach((midi, index) => {
      // Correct -- move on;
      if (midi === stack[index]) {
        weightedRightAnswers++;
        return;
      }
      weightedWrongAnswers++;
      // Blank -- label with blank, highlight in red;
      if (typeof stack[index] === 'undefined') {
        this.piano.colorNote(midi, red);
        this.piano.labelNote(midi, '_');
        return;
      }
      // Incorrect -- highlight in red;
      this.piano.colorNote(stack[index], red);
    }, this);

    return {
      question: this.instructionsDiv.innerHTML,
      questionType: 'ScalePlay',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: null,
      studentAnswerSVG: this.piano.exportSVG(),
      correctAnswerText: null,
      rightAnswers: weightedRightAnswers,
      wrongAnswers: weightedWrongAnswers,
      skills: this.questions[this.questionNumber].skills,
    };
  }

  debugRightAnswer() {
    const midi = this.question.scale.notes[this.scaleDegree].getMidiNumber();
    this.rightScaleDegree(midi);
  }

  rightScaleDegree(midiNumber) {
    // Play it back:
    if (typeof this.priorMidi !== 'undefined') {
      this.player.noteOff(this.priorMidi);
      this.piano.clearNoteColoring(this.priorMidi);
    }
    this.player.noteOn(midiNumber);
    this.priorMidi = midiNumber;

    const rightDegree = this.scaleDegree + 1;
    //      rightDegree = (rightDegree == 8) ? 1 : rightDegree;
    this.scaleDegree += this.question.direction;
    if (this.scaleDegree > 7 || this.scaleDegree < 0) {
      window.setTimeout(() => {
        this.piano.clearNoteColoring(midiNumber);
        this.player.noteOff(midiNumber);
        this.piano.clearNoteLabels();
      }, 1100);
      this.rightAnswer(1200);
    } else if (this.settings.progressTracker) {
      this.progressTracker.rightAnswer(this.questions[this.questionNumber].skills, false);
    }
    // Color the piano:
    this.piano.colorNote(midiNumber);
    if (!this.optionsTrue('hideLabels')) {
      this.piano.labelNote(midiNumber, rightDegree);
    }
  }

  // Game over:
  gameOver() {
    this.piano.removeListeners();
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    super.gameOver();
  }

  // helper function to check options variable
  optionsTrue(property) {
    return typeof this.options[property] !== 'undefined' && this.options[property] !== false;
  }

  playAnswer() {}
}

ScalePlay.registry = [
  {
    description: 'Play Major scales on the piano.',
    skills: ['major_scales_piano'],
  },
  {
    description: 'Play minor scales on the piano.',
    skills: ['natural_minor_piano', 'harmonic_minor_piano', 'melodic_minor_piano'],
  },
];

export default ScalePlay;
